import { MutableRefObject } from 'react';
import { motion } from 'framer-motion';
import { Autoplay, Keyboard } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import {
    IProductPageViewModel,
    IVariantImageViewModel,
} from 'Scripts/Types/Litium';
import { ISrcSetSize } from 'Scripts/Types/shared';

import CloseIcon from 'Icons/closeThin.svg?react';

import { ISlideModel } from './ProductGalleryContainer';
import ProductImageSlide from './ProductImageSlide';
import ProductImageThumbnails from './ProductImageThumbnails';
import ProductSliderNavigation from './ProductSliderNavigation';
import ProductSliderPagination from './ProductSliderPagination';
import ProductVideoSlide from './ProductVideoSlide';

interface IProductDetailPageMainSliderProps {
    animationDuration: number;
    sliderActive: boolean;
    animationDurationReset: number;
    selectedSwiperSlide: string;
    slides: ISlideModel[];
    setSliderActive: () => void;
    setCanInteract: () => void;
    forceRerender: () => void;
    setSelectedSwiperSlide: (id: string) => void;
    resetSlideAndClose: () => void;
    MotionProductImageSlide: any;
    swiperRef: MutableRefObject<SwiperClass | null>;
    translations: IProductPageViewModel['translations'];
    videoUrl: string;
    thumbnails: IVariantImageViewModel[];
    galleryHeight: number;
    canInteract: boolean;
    amountOfSlidesIsTwo: boolean;
    amountOfSlides: number;
    currentSliderIndex: number;
}

export const ProductDetailPageMainSlider = ({
    animationDuration,
    animationDurationReset,
    sliderActive,
    selectedSwiperSlide,
    slides,
    MotionProductImageSlide,
    swiperRef,
    translations,
    videoUrl,
    thumbnails,
    galleryHeight,
    setSliderActive,
    setCanInteract,
    forceRerender,
    setSelectedSwiperSlide,
    resetSlideAndClose,
    canInteract,
    amountOfSlidesIsTwo,
    amountOfSlides,
    currentSliderIndex,
}: IProductDetailPageMainSliderProps) => {
    const srcSetSizes: ISrcSetSize = {
        default: { width: 368, height: 521 },
        md: { width: 740, height: 1050 },
        lg: { width: 440, height: 624 },
        xl: {
            width: 648,
            height: 919,
        },
        xxl: {
            width: 994,
            height: 1409,
        },
    };

    return (
        <motion.div
            key="slidePlaceholder"
            className="flex flex-col"
            layout
            transition={{
                duration: sliderActive
                    ? animationDurationReset
                    : animationDuration,
            }}
        >
            <motion.div
                style={
                    {
                        '--galleryHeight': `-${galleryHeight}px`,
                    } as React.CSSProperties
                }
                key="sliderView"
                transition={{
                    duration: sliderActive
                        ? animationDurationReset
                        : animationDuration,
                }}
                layout
                exit={{ opacity: 0 }}
                className={
                    amountOfSlidesIsTwo
                        ? 'relative mt-0 w-[round(up,_100%,_1px)]'
                        : 'relative mt-[var(--galleryHeight)] w-[round(up,_100%,_1px)]'
                }
            >
                <motion.div
                    layoutId={selectedSwiperSlide}
                    transition={{
                        duration: sliderActive
                            ? animationDurationReset
                            : animationDuration,
                    }}
                    className="w-full bg-merino"
                    key={`motionProductSlideImage-${selectedSwiperSlide}`}
                    onLayoutAnimationComplete={() => {
                        setSliderActive();
                        setCanInteract();
                    }}
                >
                    <MotionProductImageSlide
                        key="motionProductSlidePlaceholder"
                        isPlaceholder
                        srcSetSizes={srcSetSizes}
                        index={0}
                    />
                    <MotionProductImageSlide
                        key="MotionProductImageSlide"
                        className="absolute top-0"
                        data={slides.find(x => x.id === selectedSwiperSlide)}
                        srcSetSizes={srcSetSizes}
                        index={0}
                    />
                </motion.div>
                {(sliderActive || amountOfSlidesIsTwo) && (
                    <motion.div key="SwiperWrapper">
                        {amountOfSlides > 2 && (
                            <motion.button
                                onClick={() => {
                                    resetSlideAndClose();
                                }}
                                key="swiperCloseButton"
                                className=" absolute right-6 top-6 z-[2] grid h-12 w-12 place-items-center rounded-full bg-silverLight shadow-[1px_1px_3px_#999] transition-colors duration-layout"
                            >
                                <CloseIcon className="h-6 w-6" />
                                <span className="sr-only">
                                    {translations.closeSlider}
                                </span>
                            </motion.button>
                        )}
                        <Swiper
                            onUpdate={() => {
                                const findIndex =
                                    currentSliderIndex ??
                                    slides.findIndex(
                                        y => y.id === selectedSwiperSlide
                                    );
                                const indexOrFirst =
                                    findIndex >= 0 ? findIndex : 0;

                                setSelectedSwiperSlide(
                                    slides[indexOrFirst]?.id
                                );
                            }}
                            onSwiper={swiper => {
                                swiperRef.current = swiper;
                                forceRerender();
                            }}
                            onSlideChange={x => {
                                setSelectedSwiperSlide(
                                    slides?.[x.realIndex]?.id
                                );
                            }}
                            className="absolute left-0 top-0 flex h-[round(up,_100%,_1px)]"
                            modules={[Autoplay, Keyboard]}
                            initialSlide={slides.findIndex(
                                x => x.id === selectedSwiperSlide
                            )}
                            userAgent="Server"
                            slidesPerView="auto"
                            keyboard={{
                                enabled: true,
                            }}
                            autoHeight={false}
                            loop
                            wrapperClass="flex"
                            speed={600}
                            wrapperTag="ul"
                            lazyPreloaderClass="custom-preloader"
                        >
                            {slides.map((obj, index) => (
                                <SwiperSlide
                                    tag="li"
                                    key={`${obj.id}-${index}`}
                                >
                                    {obj.videoUrl ? (
                                        <ProductVideoSlide
                                            url={obj.videoUrl}
                                            image={obj}
                                            srcSetSizes={srcSetSizes}
                                        />
                                    ) : (
                                        <ProductImageSlide
                                            data={obj}
                                            srcSetSizes={srcSetSizes}
                                        />
                                    )}
                                </SwiperSlide>
                            ))}
                            <ProductSliderNavigation
                                translations={translations}
                                swiper={swiperRef.current}
                            />
                        </Swiper>
                    </motion.div>
                )}
            </motion.div>
            <motion.div
                key="swiperExtra"
                transition={{
                    duration: sliderActive
                        ? animationDurationReset
                        : animationDuration,
                }}
                layout
            >
                <ProductSliderPagination
                    disabled={!sliderActive && !canInteract}
                    translations={translations}
                    slides={slides}
                    hasVideo={!!videoUrl}
                    swiper={swiperRef.current}
                />
                {thumbnails.length > 0 && (
                    <div className="hidden lg:grid">
                        <h3 className="my-5 text-center font-montserrat text-xl text-black">
                            {translations.moreColors}
                        </h3>
                        <ProductImageThumbnails swiper={swiperRef.current} />
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};
